import { isDefined } from "@clipboard-health/util-ts";
import { get, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

interface UseGetAllChildWorkplacesProps {
  parentFacilityId: string | undefined;
}

export const GET_FACILITY_PROFILES_API_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/facilityprofile`;

const getWorkplacesResponseSchema = z
  .object({
    data: z.array(
      z
        .object({
          type: z.literal("workplace"),
          id: z.string(),
          attributes: z.object({
            name: z.string().optional(),
            parentFacilityId: z.string().optional(),
            placementSettings: z
              .object({
                managedByParentWorkplace: z.boolean().optional(),
              })
              .optional(),
          }),
        })
        .strict()
    ),
    links: z.object({
      next: z.string().optional(),
      prev: z.string().optional(),
    }),
  })
  .strict();

export type GetWorkplacesResponse = z.infer<typeof getWorkplacesResponseSchema>;
export type GetChildWorkplacesResponse = Omit<GetWorkplacesResponse, "links">;

export function useGetAllChildWorkplaces(
  { parentFacilityId }: UseGetAllChildWorkplacesProps,
  options: UseGetQueryOptions<GetChildWorkplacesResponse> = {}
): UseQueryResult<GetChildWorkplacesResponse> {
  return useQuery({
    queryKey: [GET_FACILITY_PROFILES_API_URL, { parentFacilityId }],
    queryFn: async () => {
      // API is paginated, but we want all results immediately, so we need to fetch all pages and combine the results
      const queryParams = {
        "filter[parentFacilityId]": parentFacilityId ?? "",
        "page[size]": "1000", // max size allowed by API
      };
      const result: GetChildWorkplacesResponse = { data: [] };

      const response = await get({
        url: GET_FACILITY_PROFILES_API_URL,
        queryParams,
        responseSchema: getWorkplacesResponseSchema,
      });
      result.data.push(...response.data.data);
      let nextLink = response.data.links.next;

      while (isDefined(nextLink)) {
        // eslint-disable-next-line no-await-in-loop
        const nextResponse = await get({
          url: `${GET_FACILITY_PROFILES_API_URL}/${nextLink}`,
          queryParams,
          responseSchema: getWorkplacesResponseSchema,
        });
        result.data.push(...nextResponse.data.data);
        nextLink = nextResponse.data.links.next;
      }

      return result;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_CHILD_WORKPLACES,
    },
    enabled: isDefined(parentFacilityId),
    refetchOnWindowFocus: false, // It is *extremely* rare that the data will change
    ...options,
  });
}
